import type { TerminalOperationalSummaryResponse } from "../../../shared/production/terminal-operational";
import { quantityTotals } from "../../../shared/production/quantity";
import { isShortProductionDowntime } from "../../../shared/production/downtime";
import type { OperatorComplaint, ProductionDowntime, ProductionEntry, ProductionRun } from "./models";
import { groupedScrapQuantities } from "./operator-quantity-summary";
import { operatorQuantityRecords } from "./operator-records";
import { operatorRunTime } from "./operator-run-time";
type VisibleHistoryItem =
  | { kind: "entry"; timestamp: string; entry: ProductionEntry }
  | { kind: "downtime"; timestamp: string; downtime: ProductionDowntime }
  | { kind: "complaint"; timestamp: string; complaint: OperatorComplaint };

export function terminalSummaryRuns(response: TerminalOperationalSummaryResponse): ProductionRun[] {
  return response.items.map(({ run, ...terminalSummary }) => ({
    ...run,
    terminalSummary,
    entries: [],
    downtimes: terminalSummary.operationalDowntimes.items,
    decisions: [],
    complaints: [],
  }));
}

export function operatorRunQuantities(run: ProductionRun | null) {
  return run?.terminalSummary?.metrics.quantities ?? quantityTotals(run?.entries ?? [], run?.unit ?? "");
}

export function operatorRunLosses(run: ProductionRun) {
  return run.terminalSummary ? run.terminalSummary.metrics.groupedNonProduction : groupedScrapQuantities(run.entries);
}

export function operatorHistoryCounts(run: ProductionRun) {
  if (run.terminalSummary) return run.terminalSummary.metrics.history;
  const manual = operatorQuantityRecords(run.entries);
  const active = manual.filter(entry => !entry.voidedAt);
  const longStops = run.downtimes.filter(stop => !isShortProductionDowntime(stop));
  const visible: VisibleHistoryItem[] = [
    ...manual.map(entry => ({ kind: "entry" as const, timestamp: entry.occurredAt, entry })),
    ...longStops.map(downtime => ({ kind: "downtime" as const, timestamp: downtime.startedAt, downtime })),
    ...run.complaints.map(complaint => ({ kind: "complaint" as const, timestamp: complaint.reportedAt, complaint })),
  ];
  const latestVisibleItem = visible.reduce<VisibleHistoryItem | null>((latest, item) =>
    !latest || Date.parse(item.timestamp) > Date.parse(latest.timestamp) ? item : latest, null);
  return {
    latestVisibleItem,
    manualNonVoidedCount: active.length,
    allVisibleCount: manual.length + longStops.length + run.complaints.length,
    nonShortDowntimeCount: longStops.length,
    shortDowntimeCount: run.downtimes.length - longStops.length,
    complaintCount: run.complaints.length,
    latestManualEntry: active[0] ?? null,
  };
}

/** Rebase the display-only timing snapshot on a confirmed state transition.
 * Reason-only edits leave timing unchanged. A full summary refresh remains
 * authoritative for counts, quantities, and historical interval corrections. */
export function rebaseOperatorSummaryDowntime(run: ProductionRun, confirmed: ProductionDowntime) {
  const summary = run.terminalSummary;
  if (!summary) return undefined;
  const previous = run.downtimes.find(stop => stop.id === confirmed.id);
  if (previous && previous.startedAt === confirmed.startedAt && previous.endedAt === confirmed.endedAt) return summary;
  const at = Date.parse(confirmed.endedAt ?? confirmed.startedAt);
  const timing = operatorRunTime(run, at);
  if (!Number.isFinite(at) || !timing) return summary;
  return { ...summary, metrics: { ...summary.metrics, timing: {
    ...summary.metrics.timing, ...timing, observedAt: new Date(at).toISOString(),
  } } };
}
